import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
	deleteDocument,
	editDocumentName,
	uploadDocument,
} from 'feature/upload/uploadSlice';
import { uniqueId } from 'lodash';
import { ECarriersList } from 'types/Carrier';
import {
	IClaim,
	IClaimComponents,
	IClaimState,
	PRESCRIPTION_DEFAULT,
	TStatusCalculation,
} from 'types/Claim';
import {
	calculateEstimatesThunk,
	deleteClaimThunk,
	fixPrescription,
	getClaimDetailsThunk,
	getDoctorTypesThunk,
	getPrescriptionsThunk,
	getReasonCodeCanceledThunk,
	getReasonCodePendingThunk,
	getReasonCodeRejectThunk,
	getReasonCodeReturnThunk,
	getReasonCodeVoidThunk,
	returnClaimThunk,
	saveClaimThunk,
	submitClaimThunk,
} from './ClaimThunk';

export const getClaimDetails = createAsyncThunk(
	'claim/getClaimDetails',
	getClaimDetailsThunk,
);

export const getPrescriptions = createAsyncThunk(
	'claim/getPrescriptions',
	getPrescriptionsThunk,
);

export const saveClaim = createAsyncThunk('claim/saveClaim', saveClaimThunk);

export const submitClaim = createAsyncThunk(
	'claim/submitClaim',
	submitClaimThunk,
);

export const calculateEstimates = createAsyncThunk(
	'claim/calculateEstimates',
	calculateEstimatesThunk,
);

export const getDoctorTypes = createAsyncThunk(
	'claim/getDoctorTypes',
	getDoctorTypesThunk,
);

export const deleteClaim = createAsyncThunk(
	'claim/deleteClaim',
	deleteClaimThunk,
);

export const getReasonCodeVoid = createAsyncThunk(
	'claim/getReasonCodeVoid',
	getReasonCodeVoidThunk,
);

export const getReasonCodePending = createAsyncThunk(
	'claim/getReasonCodePending',
	getReasonCodePendingThunk,
);

export const getReasonCodeCanceled = createAsyncThunk(
	'claim/getReasonCodeCanceled',
	getReasonCodeCanceledThunk,
);

export const getReasonCodeReject = createAsyncThunk(
	'claim/getReasonCodeRejected',
	getReasonCodeRejectThunk,
);

export const getReasonCodeReturn = createAsyncThunk(
	'claim/getReasonCodeReturn',
	getReasonCodeReturnThunk,
);

export const returnClaim = createAsyncThunk(
	'claim/returnClaim',
	returnClaimThunk,
);

const initialState: IClaimState = {
	claimData: {
		carrierIntegrationFlow: null, //TODO: not used, to be removed
		customLayout: ECarriersList.DEFAULT_CARRIER,
		claimUUID: undefined,
		externalClaimId: '',
		beneficiaryMemberId: '',
		beneficiarySubscriptionUuid: '',
		memberId: '',
		memberSubscriptionUuid: '',
		providerId: '',
		claimDate: '',
		step: 0,
		// page 1
		exam: 0,
		materials: 0,
		lensType: undefined,
		rxChangedWithoutOldPrescription: null,
		//page 2
		newPrescriptionContent: undefined,
		oldPrescriptionContent: undefined,
		prescriptionConsent: false,
		//page 3
		amount: {
			//basically everything is passed by calculate. only serviceid and retail amount is not
			components: [],
			total: {
				retailAmount: '',
				retailNetOfTax: '',
				tax: '',
				total: undefined,
				contractualAdjustmentAmount: undefined,
				memberPayAmount: undefined,
				planPayAmount: undefined,
				serviceName: undefined,
				serviceId: null,
				discountedAmount: undefined,
				assessment: undefined,
				allowablePlanPay: undefined,
				coPay: undefined,
				coInsurance: undefined,
				disallowedAmount: undefined,
				decliningBalance: undefined,
				claimCostReduction: undefined,
				inNetworkSave: undefined,
			},
		},
		status: {
			id: 1,
			name: '',
			label: '',
			groupCode: '',
			groupLabel: '',
			reasonCode: '',
			reasonCodeLabel: '',
		},
		rxChangeAttestation: undefined,
		uploadConsent: false,
		attachments: {
			prescription: undefined,
			receipt: undefined,
			others: [],
		},
		receipt: null,
		claimCarrierIntegrationInfo: {
			carrierClaimId: null,
			customFaschimExtraInfo: null,
			memberFiscalCode: null,
		},
	},
	servicesMap: [],
	prescriptionSelectedFromStorage: false,
	errorCode: 0,
	errorMessage: '',
	triggerRefresh: true,
	calculated: false,
	redirect: false,
	doctorTypes: [],
	reasonCodesVoid: [],
	reasonCodesCanceled: [],
	reasonCodesPending: [],
	reasonCodesReject: [],
	reasonCodesReturn: [],
	statusCalculation: 'beforeCalculation',
	otherServices: [],
	validationErrors: [],
};

const claimSlice = createSlice({
	name: 'claim',
	initialState,
	reducers: {
		resetClaim: state => ({
			...initialState,
			reasonCodesCanceled: state.reasonCodesCanceled,
			reasonCodesVoid: state.reasonCodesVoid,
			reasonCodesPending: state.reasonCodesPending,
			reasonCodesReject: state.reasonCodesReject,
			reasonCodesReturn: state.reasonCodesReturn,
			doctorTypes: state.doctorTypes,
		}),
		setCalculated: (state, action) => ({ ...state, calculated: action.payload }),
		createClaim: (state, action) => ({
			...initialState,
			reasonCodesCanceled: state.reasonCodesCanceled,
			reasonCodesVoid: state.reasonCodesVoid,
			reasonCodesPending: state.reasonCodesPending,
			reasonCodesReject: state.reasonCodesReject,
			reasonCodesReturn: state.reasonCodesReturn,
			doctorTypes: state.doctorTypes,
			claimData: { ...initialState.claimData, ...action.payload },
		}),
		onChangeClaim: (state, action: PayloadAction<(arg0: IClaim) => IClaim>) => {
			return { ...state, claimData: action.payload(state.claimData) };
		},
		setSelectedPage: (state, action) => {
			return { ...state, claimData: { ...state.claimData, step: action.payload } };
		},
		setClaimPrescription: (state, action) => {
			const prescriptionId = action.payload.uuid;
			const prescriptionFileName = action.payload.fileName;

			return {
				...state,
				claimData: {
					...state.claimData,
					attachments: {
						...state.claimData.attachments,
						prescription: {
							uuid: prescriptionId,
							fileName: prescriptionFileName,
						},
					},
				},
				prescriptionSelectedFromStorage: true,
			};
		},
		resetPrescriptionSelectedFromStorage: state => {
			return {
				...state,
				claimData: {
					...state.claimData,
					attachments: {
						...state.claimData.attachments,
						prescription: undefined,
					},
				},
				prescriptionSelectedFromStorage: false,
			};
		},
		setStatusCalculation: (
			state,
			{ payload }: PayloadAction<TStatusCalculation>,
		) => {
			return {
				...state,
				statusCalculation: payload,
			};
		},
		setOtherServices: (state, { payload }) => {
			return {
				...state,
				otherServices: payload,
			};
		},
		setValidationErrors: (state, { payload }) => {
			return {
				...state,
				validationErrors: payload,
			};
		},
		setTriggerRefresh: (state, { payload }) => {
			return {
				...state,
				triggerRefresh: payload,
			};
		},
		resetReturnAttachment: state => {
			return {
				...state,
				claimData: {
					...state.claimData,
					attachments: {
						...state.claimData.attachments,
						return: undefined,
					},
				},
			};
		},
	},
	extraReducers: builder => {
		builder
			.addCase(getClaimDetails.fulfilled, (state, { payload }) => {
				return {
					...state,
					servicesMap: payload.servicesMap,
					claimData: {
						...payload,
						step: payload?.step || 0,
						amount: {
							...payload.amount,
							components: sortServices(payload.amount.components),
						},
						newPrescriptionContent: payload.newPrescriptionContent
							? fixPrescription(payload.newPrescriptionContent, 'FE')
							: undefined,
						oldPrescriptionContent: payload.oldPrescriptionContent
							? fixPrescription(payload.oldPrescriptionContent, 'FE')
							: undefined,
					},
					triggerRefresh: false,
					statusCalculation: payload.amount.components.find(
						(el: IClaimComponents) => el.retailAmount,
					)
						? 'afterCalculation'
						: 'beforeCalculation',
				};
			})
			.addCase(saveClaim.fulfilled, (state, { meta, payload }) => {
				return {
					...state,
					claimData: {
						...state.claimData,
						step: payload.step,
						claimUUID: payload.claimUUID,
					},
					triggerRefresh: true,
					redirect: meta.arg.redirect,
				};
			})
			.addCase(getPrescriptions.fulfilled, (state, { payload }) => {
				// prescription 0 is the current one. 1 is the last one
				const oldPrescription = fixPrescription(
					payload?.content || PRESCRIPTION_DEFAULT,
					'FE',
				);
				if (oldPrescription) {
					return {
						...state,
						claimData: {
							...state.claimData,
							oldPrescriptionContent:
								!state.claimData.rxChangeAttestation ||
								state.claimData.rxChangedWithoutOldPrescription
									? undefined
									: state.claimData.oldPrescriptionContent
										? state.claimData.oldPrescriptionContent
										: oldPrescription,
							newPrescriptionContent: state.claimData.newPrescriptionContent
								? state.claimData.newPrescriptionContent
								: {
										...oldPrescription,
										doctorTypology: null,
										prescriptionDate: null,
									},
						},
					};
				}
			})
			.addCase(submitClaim.fulfilled, (state, _) => {
				return { ...state, redirect: true };
			})
			.addCase(calculateEstimates.fulfilled, (state, { payload }) => {
				return {
					...state,
					calculated: true,
					validationErrors: [],
					claimData: {
						...state.claimData,
						amount: {
							...payload,
							components: [
								...payload.components.map((el: IClaimComponents) => {
									//this checks if the service is "other"
									//and adds a rowId to it if it is
									if (
										state.otherServices
											.map(otherEl => otherEl.serviceId)
											.includes(el.serviceId)
									) {
										return {
											...el,
											rowId: uniqueId(),
										};
									}

									return el;
								}),
							],
						},
					},
				};
			})
			.addCase(
				calculateEstimates.rejected,
				(state, { payload }: { payload: any }) => {
					const { validationErrors } = payload.data;
					state.validationErrors = validationErrors;
				},
			)
			.addCase(uploadDocument.fulfilled, (state, action) => {
				const {
					meta: {
						arg: { docType },
					},
				} = action;

				const { uuid, fileName } = action.payload;

				if (docType !== 'other') {
					state.claimData.attachments[docType] = {
						uuid,
						fileName,
					};
				} else {
					state.claimData.attachments['others'] = [
						...state.claimData.attachments['others']!,
						{ uuid, fileName },
					];
				}
				state.prescriptionSelectedFromStorage = false;
			})
			.addCase(deleteDocument.fulfilled, (state, action) => {
				const {
					meta: {
						arg: { docType, uuid },
					},
				} = action;
				if (docType !== 'other') {
					state.claimData.attachments[docType] = undefined;
				} else {
					state.claimData.attachments['others'] = state.claimData.attachments[
						'others'
					]?.filter(attachemt => attachemt.uuid !== uuid);
				}
				state.prescriptionSelectedFromStorage = false;
			})
			.addCase(editDocumentName.fulfilled, (state, action) => {
				const {
					meta: {
						arg: { docType, documentUuid },
					},
				} = action;

				if (!state.claimData) return;

				return {
					...state,
					claimData: {
						...state.claimData!,
						attachments: {
							...state.claimData!.attachments,
							[docType === 'other' ? 'others' : docType]:
								docType === 'other'
									? [
											...state.claimData.attachments.others.map(el =>
												el.uuid !== documentUuid
													? el
													: {
															uuid: documentUuid,
															fileName: action.payload.fileName,
														},
											),
										]
									: {
											uuid: documentUuid,
											fileName: action.payload.fileName,
										},
						},
					},
				};
			})
			.addCase(getDoctorTypes.fulfilled, (state, { payload }) => {
				state.doctorTypes = payload;
			})
			.addCase(getReasonCodeVoid.fulfilled, (state, { payload }) => {
				state.reasonCodesVoid = payload;
			})
			.addCase(getReasonCodePending.fulfilled, (state, { payload }) => {
				state.reasonCodesPending = payload;
			})
			.addCase(getReasonCodeCanceled.fulfilled, (state, { payload }) => {
				state.reasonCodesCanceled = payload;
			})
			.addCase(getReasonCodeReject.fulfilled, (state, { payload }) => {
				state.reasonCodesReject = payload;
			})
			.addCase(getReasonCodeReturn.fulfilled, (state, { payload }) => {
				state.reasonCodesReturn = payload;
			})
			.addCase(returnClaim.fulfilled, state => {
				return { ...state, triggerRefresh: true };
			})
			.addCase(deleteClaim.fulfilled, (state, _) => {
				return {
					...state,
					claimData: {
						...state.claimData,
						status: {
							...state.claimData.status,
							label: 'Cancelled',
						},
					},
				};
			});
	},
});

const sortServices = (
	components: IClaim['amount']['components'] | undefined,
): IClaim['amount']['components'] => {
	const sortOreder = [3, 2, 1, 5, 6, 7, 8, 9, 10, 11];
	return components
		? components.sort(
				(a, b) =>
					sortOreder.indexOf(a.serviceId!) - sortOreder.indexOf(b.serviceId!),
			)
		: [];
};

export const {
	resetClaim,
	createClaim,
	onChangeClaim,
	setCalculated,
	setSelectedPage,
	setClaimPrescription,
	resetPrescriptionSelectedFromStorage,
	setStatusCalculation,
	setOtherServices,
	setValidationErrors,
	setTriggerRefresh,
	resetReturnAttachment,
} = claimSlice.actions;

export default claimSlice.reducer;
